import { Button, Card, SelectField, StatusPill, type StatusTone } from '@homecare/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';

/**
 * The ADL and IADL ratings (§3.2, B4).
 *
 * This is the partner's real assessment form: sixteen activities, each rated
 * Dependent, Assist, Cue or Independent. `PUT /v1/clients/:id/assistance-profile`
 * has existed since session 9 and was tested; nothing in the console read or
 * wrote it, so the ratings could not be seen or entered by anybody using the
 * product. Built here, on client detail, beside allergies and medications —
 * the three together are what a caregiver is briefed from.
 *
 * ## Why it is intake data and not care-plan data
 *
 * §3.2 is explicit and it is worth repeating where the screen lives: the
 * scheduler reads these to decide **who can be assigned and how long a visit
 * takes**. A client who is DEPENDENT for transfers needs a caregiver trained
 * for it and a longer slot; one who needs a CUE for medication needs neither.
 * That is scheduling input, available from the moment of assessment, rather
 * than something that waits for a care plan to be written.
 *
 * ## The four levels, in the order an assessor thinks in
 *
 * The enum reads `DEPENDENT, INDEPENDENT, ASSIST, CUE`, which is alphabetical-
 * ish and means nothing. On the form the levels run by decreasing need —
 * dependent, assist, cue, independent — because that is the order a person
 * assessing someone moves through, and because it puts the two ends of the
 * scale at the two ends of the list. The enum is unchanged; only the display
 * order is opinionated.
 */

export type AssistanceLevel = 'DEPENDENT' | 'ASSIST' | 'CUE' | 'INDEPENDENT';

export interface AssistanceEntry {
  activity: string;
  level: AssistanceLevel;
  detail?: string | null;
}

/** Decreasing need. See the note above. */
export const LEVELS: AssistanceLevel[] = ['DEPENDENT', 'ASSIST', 'CUE', 'INDEPENDENT'];

export const LEVEL_LABEL: Record<AssistanceLevel, string> = {
  DEPENDENT: 'Dependent',
  ASSIST: 'Assist',
  CUE: 'Cue',
  INDEPENDENT: 'Independent',
};

/**
 * How much help each level means, in a caregiver's words.
 *
 * The four labels are the assessment's vocabulary and not everybody's — "cue"
 * in particular is a term of art. Spelling it out beside the control is the
 * difference between a form somebody fills in correctly and one they guess at.
 */
export const LEVEL_HINT: Record<AssistanceLevel, string> = {
  DEPENDENT: 'Someone else does it',
  ASSIST: 'Hands-on help',
  CUE: 'Reminding or supervision only',
  INDEPENDENT: 'Manages alone',
};

/**
 * Tone by level, so the two ends of the scale are legible at a glance.
 *
 * Dependent is `warning` rather than `critical`. It is not an emergency — it is
 * a great many of these clients — and spending the critical tone on a normal
 * rating would leave nothing louder for the states that are genuinely wrong.
 */
export const LEVEL_TONE: Record<AssistanceLevel, StatusTone> = {
  DEPENDENT: 'warning',
  ASSIST: 'progress',
  CUE: 'scheduled',
  INDEPENDENT: 'verified',
};

/**
 * The sixteen activities, split the way the assessment splits them.
 *
 * ADLs are self-care; IADLs are the tasks of running a household. The division
 * is not decoration: an agency's authorisation is frequently written against
 * one group and not the other, and a coordinator reading a flat list of sixteen
 * has to remember which is which.
 */
export const ACTIVITY_GROUPS: { label: string; hint: string; activities: string[] }[] = [
  {
    label: 'Daily living',
    hint: 'Personal care — what the visit is authorised for, usually.',
    activities: [
      'BATHING',
      'PERSONAL_HYGIENE',
      'TOILETING',
      'DRESSING',
      'MOBILITY_TRANSFERS',
      'EATING_DRINKING',
    ],
  },
  {
    label: 'Household and community',
    hint: 'Instrumental activities — the tasks of running a home.',
    activities: [
      'MEDICATION_REMINDER',
      'MEAL_PREPARATION',
      'LIGHT_HOUSEKEEPING',
      'GROCERY_SHOPPING',
      'LAUNDRY',
      'TRANSPORTATION',
      'HANDLING_MONEY',
      'USING_TELEPHONE',
      'READING_ASSISTANCE',
      'WASH_EQUIPMENT',
    ],
  },
];

export const ACTIVITY_LABEL: Record<string, string> = {
  BATHING: 'Bathing',
  PERSONAL_HYGIENE: 'Personal hygiene',
  TOILETING: 'Toileting',
  DRESSING: 'Dressing',
  MOBILITY_TRANSFERS: 'Mobility and transfers',
  EATING_DRINKING: 'Eating and drinking',
  MEDICATION_REMINDER: 'Medication reminders',
  MEAL_PREPARATION: 'Meal preparation',
  LIGHT_HOUSEKEEPING: 'Light housekeeping',
  GROCERY_SHOPPING: 'Grocery shopping',
  TRANSPORTATION: 'Transportation',
  LAUNDRY: 'Laundry',
  HANDLING_MONEY: 'Handling money',
  USING_TELEPHONE: 'Using the telephone',
  READING_ASSISTANCE: 'Reading assistance',
  WASH_EQUIPMENT: 'Washing equipment',
};

/** Every activity the form covers, in display order. */
export const ALL_ACTIVITIES = ACTIVITY_GROUPS.flatMap((group) => group.activities);

/**
 * One sentence about how much help this person needs.
 *
 * The count that matters to a scheduler is **hands-on**: dependent and assist
 * together decide whether a visit needs a trained caregiver and a longer slot.
 * Cue and independent do not, so they are not what the sentence leads with.
 *
 * Pure, and exported, so the reading is asserted without a browser.
 */
export function assistanceSummary(entries: readonly AssistanceEntry[]): {
  sentence: string;
  assessed: number;
  handsOn: number;
} {
  const rated = entries.filter((entry) => ALL_ACTIVITIES.includes(entry.activity));
  const handsOn = rated.filter(
    (entry) => entry.level === 'DEPENDENT' || entry.level === 'ASSIST',
  ).length;

  if (rated.length === 0) {
    return {
      sentence: 'Not assessed yet. A scheduler cannot size a visit without this.',
      assessed: 0,
      handsOn: 0,
    };
  }

  const remaining = ALL_ACTIVITIES.length - rated.length;
  const tail = remaining === 0 ? '' : ` ${remaining} not yet assessed.`;

  if (handsOn === 0) {
    return {
      sentence: `No hands-on help needed across ${rated.length} assessed activities.${tail}`,
      assessed: rated.length,
      handsOn,
    };
  }

  return {
    sentence:
      `Hands-on help with ${handsOn} of ${rated.length} assessed ` +
      `${rated.length === 1 ? 'activity' : 'activities'}.${tail}`,
    assessed: rated.length,
    handsOn,
  };
}

/**
 * The card on client detail.
 *
 * Read by default and edited in place, rather than on a separate route: the
 * whole thing is sixteen selects, and sending somebody to another screen to
 * change one rating would be the longer path for the commoner action.
 */
export function AssistanceProfileCard({
  clientId,
  entries,
  canEdit,
  restricted,
}: {
  clientId: string;
  entries: AssistanceEntry[] | undefined;
  canEdit: boolean;
  /** True when the caller lacks `client:read_phi`, so the API omitted the key. */
  restricted: boolean;
}): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, AssistanceLevel | ''>>({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const recorded = entries ?? [];
  const byActivity = new Map(recorded.map((entry) => [entry.activity, entry]));
  const summary = assistanceSummary(recorded);

  const startEditing = (): void => {
    setDraft(
      Object.fromEntries(
        ALL_ACTIVITIES.map((activity) => [activity, byActivity.get(activity)?.level ?? '']),
      ),
    );
    setFailure(null);
    setEditing(true);
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);

    try {
      // Unrated activities are omitted rather than sent as a null level. "Not
      // assessed" and "assessed as independent" are different clinical facts,
      // and the contract has no level that means the first one.
      const next = ALL_ACTIVITIES.filter((activity) => draft[activity]).map((activity) => ({
        activity,
        level: draft[activity] as AssistanceLevel,
      }));

      await api.send('PUT', `/v1/clients/${clientId}/assistance-profile`, { entries: next });
      await queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] });
      setEditing(false);
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-ink text-lg font-bold">Assistance profile</h2>
          <p className="text-ink-3 mt-1 text-sm">
            What this person needs help with, and how much. The scheduler reads it to size a visit
            and to decide who can be sent.
          </p>
        </div>

        {canEdit && !restricted && !editing && (
          <Button variant="secondary" size="sm" onClick={startEditing}>
            {summary.assessed === 0 ? 'Record an assessment' : 'Edit'}
          </Button>
        )}
      </div>

      {restricted ? (
        // The API omits the key entirely rather than sending an empty array —
        // layer four, §8. Saying so is better than rendering "not assessed",
        // which would be a claim about the client rather than about the reader.
        <p className="text-ink-3 mt-3 text-sm">
          Hidden. Seeing an assistance profile needs the clinical-records permission.
        </p>
      ) : editing ? (
        <div className="mt-4 grid gap-5">
          <ErrorNote error={failure} nextStep="Check the ratings and save again." />

          {ACTIVITY_GROUPS.map((group) => (
            <fieldset key={group.label} className="grid gap-3">
              <legend className="text-ink text-sm font-semibold">{group.label}</legend>
              <p className="text-ink-3 -mt-1 text-xs">{group.hint}</p>

              <div className="grid gap-3 sm:grid-cols-2">
                {group.activities.map((activity) => (
                  <SelectField
                    key={activity}
                    label={ACTIVITY_LABEL[activity] ?? activity}
                    value={draft[activity] ?? ''}
                    onChange={(event) =>
                      setDraft((was) => ({
                        ...was,
                        [activity]: event.target.value as AssistanceLevel | '',
                      }))
                    }
                  >
                    <option value="">Not assessed</option>
                    {LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {LEVEL_LABEL[level]} — {LEVEL_HINT[level]}
                      </option>
                    ))}
                  </SelectField>
                ))}
              </div>
            </fieldset>
          ))}

          <div className="flex items-center gap-2">
            <Button onClick={() => void save()} pending={saving}>
              Save assessment
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 grid gap-4">
          <p className="text-ink-2 text-sm">{summary.sentence}</p>

          {summary.assessed > 0 &&
            ACTIVITY_GROUPS.map((group) => {
              const rated = group.activities.filter((activity) => byActivity.has(activity));
              if (rated.length === 0) return null;

              return (
                <div key={group.label}>
                  <h3 className="eyebrow">{group.label}</h3>
                  <ul className="mt-2 grid gap-1.5">
                    {rated.map((activity) => {
                      const entry = byActivity.get(activity)!;
                      return (
                        <li key={activity} className="flex items-center justify-between gap-3">
                          <span className="text-ink-2 text-sm">
                            {ACTIVITY_LABEL[activity] ?? activity}
                            {entry.detail && (
                              <span className="text-ink-3 block text-xs">{entry.detail}</span>
                            )}
                          </span>
                          <StatusPill tone={LEVEL_TONE[entry.level]}>
                            {LEVEL_LABEL[entry.level]}
                          </StatusPill>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
        </div>
      )}
    </Card>
  );
}
